import Icon, { IconType } from '../icon/Icon';

import classNames from 'classnames';

export enum TableHeadCellSorting {
  DEFAULT = '',
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

export enum TableHeadCellColumnSize {
  TINY = 'tiny',
  SMALL = 'small',
  LARGE = 'large',
  HUGE = 'huge',
  XHUGE = '2xhuge',
}

export interface TableHeadCellProps {
  field: string;
  label?: string;
  columnSize?: TableHeadCellColumnSize;
  isLeftSticky?: boolean;
  isRightSticky?: boolean;
  isLastLeftSticky?: boolean;
  isFirstRightSticky?: boolean;
  stickyPosition?: number;
  sort?: TableHeadCellSorting;
  onSort?: () => void;
  onScroll?: (direction: 'left' | 'right') => void;
  cellRenderer?: (data: {
    rowData: any;
    colDefs: any;
    value: any;
  }) => React.ReactNode;
}

const TableHeadCell = ({
  label,
  columnSize,
  isLeftSticky = false,
  isRightSticky = false,
  isLastLeftSticky = false,
  isFirstRightSticky = false,
  stickyPosition,
  sort,
  onSort,
  onScroll,
  cellRenderer, // Used for custom cell rendering in the body rows.
}: TableHeadCellProps) => {
  const handleScrollClick = (direction: 'left' | 'right') => {
    if (onScroll) {
      onScroll(direction);
    }
  };

  const getStyles = () => {
    if (stickyPosition === undefined) return {};

    if (isLeftSticky) {
      return { left: `${stickyPosition}px` };
    }

    if (isRightSticky) {
      return { right: `${stickyPosition}px` };
    }

    return {};
  };

  const showLeftControl = isLeftSticky && isLastLeftSticky;
  const showRightControl = isRightSticky && isFirstRightSticky;

  return (
    <div
      className={classNames('m-table__th', {
        [`m-table__column--${columnSize}`]: columnSize,
        'm-table__column--pinned': isLeftSticky,
        'm-table__column--pinned-right': isRightSticky,
      })}
      style={getStyles()}
      role="columnheader"
    >
      {sort != null ? (
        <button
          type="button"
          className={classNames('m-table__head-btn', {
            'm-table__head-btn--down': sort === TableHeadCellSorting.ASCENDING,
            'm-table__head-btn--up': sort === TableHeadCellSorting.DESCENDING,
          })}
          onClick={() => onSort && onSort()}
        >
          {label}
          <svg
            aria-hidden="true"
            className="u-icon-micro"
            width="32"
            height="33"
            viewBox="0 0 32 33"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M15.5 30L7.27276 18.75H23.7272L15.5 30Z" />
            <path d="M15.5 3L23.7272 14.25H7.27276L15.5 3Z" />
          </svg>
        </button>
      ) : (
        label
      )}
      {(showLeftControl || showRightControl) && (
        <button
          type="button"
          className={classNames('m-table__control', {
            'm-table__control--left': showLeftControl,
            'm-table__control--right': showRightControl,
          })}
          onClick={() => handleScrollClick(showLeftControl ? 'left' : 'right')}
          aria-label={`Scroll ${showLeftControl ? 'left' : 'right'}`}
        >
          {showLeftControl && (
            <Icon iconType={IconType.ArrowLeft} className="u-icon-tiny" />
          )}
          {showRightControl && (
            <Icon iconType={IconType.ArrowRight} className="u-icon-tiny" />
          )}
        </button>
      )}
    </div>
  );
};

export default TableHeadCell;
